import { InMemoryCache } from '@apollo/client';
import { gate } from 'hub-http/gates';
import { userInfoSync } from 'hub-http/userInfo';
import { Metrics } from './Metrics';
import { didRhumbAlreadyFire, watchForRhumbMark } from './rhumbObserver';
const CACHE_PERF_GATE = gate('hs:apollo:cache-perf-metrics');
const getOperationName = query => {
  var _query$definitions$fi, _query$definitions;
  return (_query$definitions$fi = (_query$definitions = query.definitions) === null || _query$definitions === void 0 || (_query$definitions = _query$definitions.find(d => d.kind === 'OperationDefinition')) === null || _query$definitions === void 0 || (_query$definitions = _query$definitions.name) === null || _query$definitions === void 0 ? void 0 : _query$definitions.value) !== null && _query$definitions$fi !== void 0 ? _query$definitions$fi : 'unknown';
};
let aggregatesByLocation = {};
const getAggregates = location => {
  if (!aggregatesByLocation[location]) {
    const alreadyFired = didRhumbAlreadyFire();
    aggregatesByLocation[location] = {
      writeMs: 0,
      diffMs: 0,
      flushed: alreadyFired,
      observerSetup: alreadyFired
    };
  }
  return aggregatesByLocation[location];
};
export const __test_only_clearAggregates = () => {
  aggregatesByLocation = {};
};
export class HubspotInMemoryCache extends InMemoryCache {
  constructor(config) {
    super(config);
    this.enabled = null;
    this.slowThreshold = null;
    this.location = config.location;
    this.slowThreshold = this.getSlowThreshold();

    // If the user has set a slow query threshold, enable logging automatically
    if (this.slowThreshold !== null) {
      this.enabled = true;
      this.setupRhumbObserver();
    }
  }
  getSlowThreshold() {
    try {
      const stored = localStorage.getItem(`apollo_cache_slow_log_threshold_${this.location}`);
      if (stored !== null) {
        const parsed = parseFloat(stored);
        return isNaN(parsed) ? null : parsed;
      }
    } catch (_unused) {
      // localStorage access can throw in some contexts
    }
    return null;
  }
  timer(name, ms) {
    Metrics.timer(name, {
      location: this.location
    }).update(ms);
  }
  isEnabled() {
    if (this.enabled === null) {
      try {
        const {
          gates
        } = userInfoSync({
          ignoreRedirect: true
        });
        this.enabled = gates.includes(CACHE_PERF_GATE);
      } catch (_unused2) {
        this.enabled = false;
      }
      if (this.enabled) {
        this.setupRhumbObserver();
      }
    }
    return this.enabled;
  }
  flushAggregates() {
    const agg = getAggregates(this.location);
    if (agg.flushed) {
      return;
    }
    agg.flushed = true;
    const combined = agg.writeMs + agg.diffMs;
    this.timer('cache-write-aggregate', agg.writeMs);
    this.timer('cache-diff-aggregate', agg.diffMs);
    this.timer('cache-time-aggregate', combined);
    if (this.slowThreshold !== null) {
      console.log(`[${this.location}] [a] ${agg.writeMs.toFixed(1)}ms write, ${agg.diffMs.toFixed(1)}ms diff, ${combined.toFixed(1)}ms total`);
    }
  }
  setupRhumbObserver() {
    const agg = getAggregates(this.location);
    if (agg.observerSetup) {
      return;
    }
    agg.observerSetup = true;
    watchForRhumbMark(() => this.flushAggregates());
  }

  /** Instruments the normalization path — StoreWriter.writeToStore() breaks
   *  GraphQL results into flat normalized entities keyed by cache ID. */
  write(options) {
    if (!this.isEnabled()) {
      return super.write(options);
    }
    const start = performance.now();
    const result = super.write(options);
    const elapsed = performance.now() - start;
    this.timer('cache-write', elapsed);
    if (this.slowThreshold !== null && elapsed >= this.slowThreshold) {
      console.log(`[${this.location}] [w] ${getOperationName(options.query)} (${elapsed.toFixed(1)}ms)`, {
        query: options.query,
        result: options.result
      });
    }
    const agg = getAggregates(this.location);
    if (!agg.flushed) {
      agg.writeMs += elapsed;
    }
    return result;
  }

  /** Instruments the read + canonization path. readQuery/readFragment and Apollo's
   *  query watching all delegate to diff(), making it the single chokepoint for
   *  all cache reads. With canonizeResults:true, this includes ObjectCanon.admit(). */
  diff(options) {
    if (!this.isEnabled()) {
      return super.diff(options);
    }
    const start = performance.now();
    const result = super.diff(options);
    const elapsed = performance.now() - start;
    this.timer('cache-diff', elapsed);
    if (this.slowThreshold !== null && elapsed >= this.slowThreshold) {
      console.log(`[${this.location}] [d] ${getOperationName(options.query)} (${elapsed.toFixed(1)}ms)`, {
        query: options.query,
        result: result.result
      });
    }
    const agg = getAggregates(this.location);
    if (!agg.flushed) {
      agg.diffMs += elapsed;
    }
    return result;
  }
}